/**
 * Dialog Data Prefetch Service
 * 
 * This service manages prefetching and caching of data required by Edit Driver
 * and Edit Value dialogs. Data is fetched in the background on Plan Details page
 * entry, final save, and grid refresh.
 * 
 * The service ensures:
 * - Data is fetched independently of dialog UI state
 * - No duplicate API calls
 * - Background failures don't impact Plan Details page UX
 * - Dialogs consume prefetched data only (no API calls on open)
 */

import Http from "./Http";
import dataService from "./data.service";
import {uniqueFlatDepartmentsFromPlanRows} from "../Utils/planDepartmentDropdownOptions";

class DialogDataPrefetchService {
  constructor() {
    this.cache = {
      departments: null,
      departmentPlanRows: null,
      accountTypes: null,
      structureCodes: null,
      revenueSegmentStructureCodes: null,
      glCodes: null,
      structureCodeMappings: null,
      revenuePairedFunctionalCodes: null,
      revenuePairedStructureCodes: null,
      planStatisticalCodeIds: null,
      planId: null,
      organizationId: null,
      templateId: null,
      lastFetchTimestamp: null,
    };
    
    this.fetchingRef = {
      isFetching: false,
      abortController: null,
      promise: null,
      generation: 0,
      currentPlanId: null,
      currentOrgId: null,
    };
    /** In-flight GET department-mappings/with-sub-departments keyed by `${orgId}:${planId}` */
    this.departmentPlanRowsInFlight = new Map();

    // Invalidate cached dialog data when reference data changes elsewhere (e.g. a
    // department-mapping save dispatches this event), so the next prefetch refetches
    // instead of serving stale mappings.
    if (typeof window !== "undefined" && window.addEventListener) {
      window.addEventListener("tpx-department-mapping-updated", () => this.invalidate());
    }
  }

  /**
   * Load plan-scoped department rows (with sub-departments) once per org/plan, with
   * in-flight deduplication. Updates cache the same way as full prefetch. Safe to
   * call from Plan Details (e.g. Ag grid set filter) and in parallel with prefetch.
   * @param {string|number} planId
   * @param {string|number} organizationId
   * @returns {Promise<Array>}
   */
  ensureDepartmentPlanRows(planId, organizationId) {
    if (planId == null || organizationId == null) {
      return Promise.resolve([]);
    }
    const pid = String(planId);
    const oid = String(organizationId);
    if (
      this.cache.departmentPlanRows != null &&
      Array.isArray(this.cache.departmentPlanRows) &&
      String(this.cache.planId) === pid &&
      String(this.cache.organizationId) === oid
    ) {
      return Promise.resolve(this.cache.departmentPlanRows);
    }
    const key = `${oid}:${pid}`;
    if (this.departmentPlanRowsInFlight.has(key)) {
      return this.departmentPlanRowsInFlight.get(key);
    }
    const p = Http.GET(
      `department-mappings/with-sub-departments?hmgId=${organizationId}&planId=${planId}`
    )
      .then((res) => {
        if (res?.status === 200 && Array.isArray(res.data)) {
          this.cache.departmentPlanRows = res.data;
          this.cache.departments = uniqueFlatDepartmentsFromPlanRows(res.data);
          this.cache.planId = planId;
          this.cache.organizationId = organizationId;
          return res.data;
        }
        return [];
      })
      .catch((err) => {
        console.warn("ensureDepartmentPlanRows failed (non-critical):", err);
        return [];
      })
      .finally(() => {
        this.departmentPlanRowsInFlight.delete(key);
      });
    this.departmentPlanRowsInFlight.set(key, p);
    return p;
  }

  /**
   * Get cached data
   * @returns {Object} Cached data object
   */
  getCachedData() {
    return { ...this.cache };
  }

  /**
   * Check if data is stale or missing
   * @param {string} planId - Current plan ID
   * @param {string} organizationId - Current organization ID
   * @returns {boolean} True if data needs refresh
   */
  needsRefresh(planId, organizationId) {
    if (!planId || !organizationId) return true;
    if (this.cache.planId !== planId || this.cache.organizationId !== organizationId) return true;
    if (!this.cache.departments || !this.cache.departmentPlanRows || !this.cache.accountTypes || !this.cache.structureCodes) return true;
    return false;
  }

  /**
   * Prefetch all data required for Edit Driver and Edit Value dialogs
   * Runs in background, errors are handled silently
   * 
   * @param {string} planId - Plan ID
   * @param {string} organizationId - Organization ID
   * @param {Function} onComplete - Optional callback when fetch completes
   * @returns {Promise<void>}
   */
  prefetchDialogData(planId, organizationId, optionsOrOnComplete = null) {
    // Backward-compatible 3rd arg: existing callers (Edit Value dialogs) pass a bare
    // onComplete function; newer callers pass an options object { plan, onComplete }.
    const options = typeof optionsOrOnComplete === "function"
      ? { onComplete: optionsOrOnComplete }
      : (optionsOrOnComplete || {});
    const { plan = null, onComplete = null } = options;

    // A prefetch for the SAME plan/org is already running: reuse it (in-flight de-dup); onComplete
    // still fires when it settles. For a DIFFERENT target, supersede it — abort the stale request
    // and start fresh (M-02: previously the signal was wired but abort() was never called, and a
    // different-target caller wrongly reused plan A's in-flight promise).
    if (this.fetchingRef.isFetching && this.fetchingRef.promise) {
      const sameTarget =
        String(this.fetchingRef.currentPlanId) === String(planId) &&
        String(this.fetchingRef.currentOrgId) === String(organizationId);
      if (sameTarget) {
        const inFlight = this.fetchingRef.promise;
        if (onComplete) inFlight.finally(() => onComplete());
        return inFlight;
      }
      if (this.fetchingRef.abortController) {
        this.fetchingRef.abortController.abort();
      }
    }

    const myGen = (this.fetchingRef.generation || 0) + 1;
    this.fetchingRef.generation = myGen;
    this.fetchingRef.isFetching = true;
    this.fetchingRef.currentPlanId = planId;
    this.fetchingRef.currentOrgId = organizationId;
    this.fetchingRef.abortController = new AbortController();
    const signal = this.fetchingRef.abortController.signal;

    const run = async () => {
    try {
      const savedHmgId = localStorage.getItem('selectedHmgId');

      // Prefer the plan object passed in by the caller (Plan Details already fetched it)
      // to avoid a duplicate GET plan/{id}. Fall back to other sources only if absent.
      let templateId = plan?.ccmsTemplateId || null;
      if (!templateId) {
        templateId = localStorage.getItem('selectedTemplateId') ||
                    (dataService.getSelectedTemplate && dataService.getSelectedTemplate()?.value) ||
                    null;
      }

      // Fetch all APIs in parallel (statistical-code-ids with departments / structure codes for plan)
      const [
        ,
        accountTypesData,
        structureCodesResponse,
        glCodesResponse,
        structureCodeMappings,
        revenuePairedFunctionalCodesResponse,
        statisticalCodeIdsResponse,
      ] = await Promise.all([
        this.ensureDepartmentPlanRows(planId, organizationId),
        // Route account-types through the shared cached/de-duped getter so the page and this
        // prefetch share one request instead of each fetching it (TPX-1856). Returns a raw array.
        dataService.getAllAccountTypesData(),
        // Single all-structure-code call (no ?isOnlySegmented). VERIFIED (TPX-1856, localhost.har)
        // the backend returns identical data with/without the flag, so its payload is reused below
        // for both the general and revenue-segment structure-code lists.
        Http.GET("chart-of-accounts/all-structure-code?hmgId=" + savedHmgId, {}, { signal, _silent: true }),
        // Use new endpoint with department info if templateId is available, otherwise fallback
        (organizationId && templateId && planId)
          ? Http.GET(`chart-of-accounts/functional-codes-with-department/${planId}`, {}, { signal, _silent: true })
          : organizationId
            ? Http.GET(`/chart-of-accounts/functional-codes/${organizationId}`, {}, { signal, _silent: true })
            : Promise.resolve({ data: [] }),
        organizationId ? Http.GET("chart-of-accounts/all-structure-code-with-account-structure-mappings?hmgId=" + organizationId, {}, { signal, _silent: true }) : Promise.resolve({ data: null }),
        // Fetch revenue paired functional codes for driver type 12
        planId ? Http.GET(`chart-of-accounts/functional-codes/revenue-paired-functional-codes/${planId}`, {}, { signal, _silent: true }) : Promise.resolve({ data: [] }),
        planId ? Http.GET(`plan-details/${planId}/statistical-code-ids`, {}, { signal, _silent: true }) : Promise.resolve({ status: null, data: null }),
      ]);

      // Department rows: already cached by ensureDepartmentPlanRows

      // Account types arrive from the shared getter as a raw array.
      if (Array.isArray(accountTypesData)) {
        this.cache.accountTypes = accountTypesData.map((m) => ({
          id: m.id,
          typeName: m.accountTypeName,
        }));
      }

      if (structureCodesResponse?.status === 200) {
        const mappedStructureCodes = structureCodesResponse.data.map((m) => ({
          id: m.ccmsStructureCodeId,
          structureCodeName: m.structureCodeName,
        }));
        this.cache.structureCodes = mappedStructureCodes;
        // Reuse the SAME payload for the revenue-segment list — see the single-call note above.
        // REVISIT: if the backend makes ?isOnlySegmented actually filter, restore a separate call.
        this.cache.revenueSegmentStructureCodes = mappedStructureCodes;
      }

      // Process GL codes - handle both endpoints (with and without department info)
      if (glCodesResponse?.data) {
        const codesMap = new Map();
        glCodesResponse.data.forEach(fc => {
          const id = fc.ccmsFunctionalCodeId || fc.functionalCodeId;
          const departmentId = fc.departmentId || null;
          
          if (!codesMap.has(id)) {
            codesMap.set(id, {
              id: id,
              functionalCode: fc.functionalCode,
              functionalCodeName: fc.functionalCodeName,
              description: fc.description,
              displayName: `${fc.functionalCode} - ${fc.functionalCodeName}`,
              departmentId: departmentId,
              departmentIds: departmentId ? [departmentId] : []
            });
          } else {
            const existing = codesMap.get(id);
            if (departmentId && !existing.departmentIds.includes(departmentId)) {
              existing.departmentIds.push(departmentId);
            }
          }
        });
        this.cache.glCodes = Array.from(codesMap.values());
      }

      // Process structure code mappings
      if (structureCodeMappings?.status === 200 && structureCodeMappings.data) {
        let tmpObjectsMap = {};
        
        if (structureCodeMappings.data.accountStructureCodeMappings && Array.isArray(structureCodeMappings.data.accountStructureCodeMappings)) {
          tmpObjectsMap = structureCodeMappings.data.accountStructureCodeMappings.reduce((map, item) => {
            const { ccmsStructureCodeId, departmentId, accountTypeId } = item;
            
            if (!map.hasOwnProperty(ccmsStructureCodeId)) {
              map[ccmsStructureCodeId] = [];
            }
            
            map[ccmsStructureCodeId].push({
              structureCodeId: ccmsStructureCodeId,
              departmentId,
              accountTypeId
            });
            
            return map;
          }, tmpObjectsMap);
        }
        
        this.cache.structureCodeMappings = tmpObjectsMap;
      }

      if (statisticalCodeIdsResponse?.status === 200 && statisticalCodeIdsResponse.data) {
        const d = statisticalCodeIdsResponse.data;
        const toNumArr = (v) => {
          if (v == null) return [];
          if (Array.isArray(v)) return v.map((x) => Number(x));
          return [...v].map((x) => Number(x));
        };
        this.cache.planStatisticalCodeIds = {
          structureCodeIds: toNumArr(d.structureCodeIds),
          glCodeIds: toNumArr(d.glCodeIds),
        };
      }

      // Process revenue paired functional codes and structure codes for driver type 12
      if (revenuePairedFunctionalCodesResponse?.status === 200 && revenuePairedFunctionalCodesResponse.data) {
        const responseData = revenuePairedFunctionalCodesResponse.data;
        
        // Handle new response structure with functionalCodes and structureCodes
        if (responseData.functionalCodes && Array.isArray(responseData.functionalCodes)) {
          const transformedCodes = responseData.functionalCodes.map((fc) => ({
            id: fc.ccmsFunctionalCodeId,
            functionalCode: fc.functionalCode,
            functionalCodeName: fc.functionalCodeName,
            displayName: `${fc.functionalCode} - ${fc.functionalCodeName}`,
            departmentId: fc.departmentId,
            departmentName: fc.departmentName
          }));
          this.cache.revenuePairedFunctionalCodes = transformedCodes;
        }
        
        // Handle structure codes from new response structure
        if (responseData.structureCodes && Array.isArray(responseData.structureCodes)) {
          const transformedStructureCodes = responseData.structureCodes.map((sc) => ({
            id: sc.ccmsStructureCodeId,
            structureCode: sc.structureCode,
            structureCodeName: sc.structureCodeName,
            displayName: sc.structureCodeName,
            departmentsAndAccountTypes: sc.departmentsAndAccountTypes || []
          }));
          this.cache.revenuePairedStructureCodes = transformedStructureCodes;
        }
      }

      // Update cache metadata
      this.cache.planId = planId;
      this.cache.organizationId = organizationId;
      this.cache.templateId = templateId;
      this.cache.lastFetchTimestamp = Date.now();

    } catch (error) {
      // Aborted (superseded by a newer plan/org) is intentional — stay quiet. Otherwise log.
      if (error?.code !== 'ERR_CANCELED' && error?.name !== 'CanceledError') {
        // Silently handle errors - don't disrupt Plan Details page UX
        console.warn('Background prefetch failed (non-critical):', error);
      }
      // Don't clear cache on error - keep existing data if available
    } finally {
      // Only the latest run resets shared fetch state, so a superseded (aborted) run's finally
      // can't clobber the newer run's isFetching/abortController/promise (M-02).
      if (myGen === this.fetchingRef.generation) {
        this.fetchingRef.isFetching = false;
        this.fetchingRef.abortController = null;
        this.fetchingRef.promise = null;
      }

      if (onComplete) {
        onComplete();
      }
    }
    };

    this.fetchingRef.promise = run();
    return this.fetchingRef.promise;
  }

  /**
   * Mark cached dialog data stale so the next prefetch refetches. Called when reference
   * data that dialogs depend on changes (department-mapping save, COA mapping publish).
   */
  invalidate() {
    this.clearCache();
  }

  /**
   * Clear cache (useful when switching plans/organizations)
   */
  clearCache() {
    this.departmentPlanRowsInFlight = new Map();
    this.cache = {
      departments: null,
      departmentPlanRows: null,
      accountTypes: null,
      structureCodes: null,
      revenueSegmentStructureCodes: null,
      glCodes: null,
      structureCodeMappings: null,
      revenuePairedFunctionalCodes: null,
      revenuePairedStructureCodes: null,
      planStatisticalCodeIds: null,
      planId: null,
      organizationId: null,
      templateId: null,
      lastFetchTimestamp: null,
    };
  }
}

// Export singleton instance
const dialogDataPrefetchService = new DialogDataPrefetchService();
export default dialogDataPrefetchService;

